export type ClientErrorReportInput = {
  componentStack?: string | null;
  currentPath?: string | null;
  errorCause?: string | null;
  errorId?: string | null;
  /** Kurzform Datei:Zeile:Spalte bzw. Komponente. */
  errorLocation?: string | null;
  errorMessage?: string | null;
  /** Modulname eines gebündelten Dateinamens (`TasksPage-vlM7DJ_k.js` → `TasksPage`). */
  moduleHint?: string | null;
  requestId?: string | null;
  stack?: string | null;
};

export type ClientErrorReportLabels = {
  errorId: string;
  requestId: string;
  timestamp: string;
  currentPath: string;
  errorLocation: string;
  moduleHint: string;
  errorMessage: string;
  errorCause: string;
  url: string;
  userAgent: string;
  stack: string;
  componentStack: string;
};

/**
 * Der eine Fehlerbericht zum Kopieren: zuerst die Suchfelder für den Support, dann URL, Browser
 * und die vollständigen Stacks.
 */
export function buildErrorReportText(parts: ClientErrorReportInput, labels: ClientErrorReportLabels): string {
  const head: string[] = [];
  if (parts.errorId) head.push(`${labels.errorId}: ${parts.errorId}`);
  if (parts.requestId) head.push(`${labels.requestId}: ${parts.requestId}`);
  if (parts.currentPath) head.push(`${labels.currentPath}: ${parts.currentPath}`);
  if (parts.errorLocation) {
    const hint = parts.moduleHint ? ` (${labels.moduleHint}: ${parts.moduleHint})` : "";
    head.push(`${labels.errorLocation}: ${parts.errorLocation}${hint}`);
  }
  if (parts.errorMessage) head.push(`${labels.errorMessage}: ${parts.errorMessage}`);
  if (parts.errorCause) head.push(`${labels.errorCause}: ${parts.errorCause}`);
  head.push(`${labels.timestamp}: ${new Date().toISOString()}`);

  const blocks = [head.join("\n")];
  if (typeof window !== "undefined") {
    blocks.push(`${labels.url}: ${window.location.href}\n${labels.userAgent}: ${navigator.userAgent}`);
  }
  if (parts.stack?.trim()) blocks.push(`${labels.stack}:\n${parts.stack.trim()}`);
  if (parts.componentStack?.trim()) blocks.push(`${labels.componentStack}:\n${parts.componentStack.trim()}`);

  return blocks.join("\n\n");
}

const sourceLocationPattern = /([^/\s()]+\.(?:tsx?|jsx?|js)):(\d+):(\d+)/;

/** Erster Ort aus Stack (Datei:Zeile:Spalte) oder erste Komponente des Component-Stacks. */
export function readErrorLocation(stack?: string | null, componentStack?: string | null): string | null {
  const match = stack?.match(sourceLocationPattern);
  if (match) return `${match[1]}:${match[2]}:${match[3]}`;
  const component = componentStack?.trim().split("\n")[0]?.trim().replace(/^(at|in)\s+/, "");
  return component || null;
}

/** Bei gebündelten Dateinamen den Modulnamen für Lesbarkeit. */
export function readModuleHint(location: string | null): string | null {
  return location?.match(/^([A-Za-z0-9_]+)-[a-zA-Z0-9_]+\.js/)?.[1] ?? null;
}
